import queryString from "query-string";
import { payment, teams } from "../constants/actionTypes";

const defaultPreloaders = {
  addCard: null,
  chargeForMembership: null,
  deleteCard: null,
  paymentMethods: null,
  paymentHistory: null,
  // The links of the invoice PDFs being fetched (a list, so two rows can download at once). Not paymentHistory: that one
  // is the list's own request.
  invoicePdf: [],
  updatePaymentMethod: null,
  updateCard: null
};

const isDefaultCard = card => card.default_source === true || card.default_source === 1 || card.default_source === "true";

const resolveSelectedCard = (cards, currentSelected) => {
  const selectedFromPayload = currentSelected?.id ? cards?.find(card => card.id === currentSelected.id) : null;
  if (selectedFromPayload) {
    return selectedFromPayload;
  }
  return cards?.find(isDefaultCard) || cards?.[0] || null;
};

const defaultState = {
  defaultSource: null,
  error: {
    addCard: null,
    chargeForMembership: null,
    deleteCard: null,
    paymentMethods: null,
    paymentHistory: null,
    updateCard: null,
    updatePaymentMethod: null,
    stripeTokenError: null
  },
  paymentMethods: null,
  // GROWTH-6647: the last cards-all answer came without the card list (Stripe could not be reached), so `paymentMethods` is
  // empty because it is unknown, not because the account has no cards. Its subscriptions are real.
  cardsUnavailable: false,
  subscriptions: [],
  cancelledSubscriptions: [],
  paymentHistory: [],
  preloaders: defaultPreloaders,
  paymentError: null,
  selectedCard: null,
  success: {
    addCard: null,
    chargeForMembership: null,
    deleteCard: null,
    paymentMethods: null,
    paymentHistory: null,
    updatePaymentMethod: null,
    updateCard: null
  },
  paymentHistoryPagination: {},
  stripeToken: "",
  subscriptionLoadingActive: false,
  // The account whose cards, subscriptions and history this slice holds: a team owner's user_id, or "" for the member's
  // own account. Kept in step with teams.currentTeam by the SET_TEAM case below.
  ownerId: ""
};

export default function(state = defaultState, action) {
  switch (action.type) {
    case payment.SET_LOADING:
      return {
        ...state,
        preloaders: { ...state.preloaders, [action.payload.section]: action.payload.isSectionLoading }
      };

    case payment.GET_PAYMENT_METHODS: {
      const { cards, cancelledSubscriptions = [] } = action.payload;

      return {
        ...state,
        paymentMethods: cards,
        cardsUnavailable: Boolean(action.payload.cardsUnavailable),
        subscriptions: action.payload.subscriptions,
        expiredSubscriptions: action.payload.expiredSubscriptions,
        cancelledSubscriptions,
        selectedCard: resolveSelectedCard(cards, state.selectedCard),
        // GROWTH-6647: an answer ends an earlier failure, so a Retry that works leaves nothing of the error panel behind.
        paymentError: null,
        preloaders: {
          ...state.preloaders,
          paymentMethods: false
        }
      };
    }

    case payment.SET_SELECTED_CARD:
      return {
        ...state,
        selectedCard: action.payload
      };

    case payment.GET_PAYMENT_METHODS_FAIL:
      return {
        ...state,
        // GROWTH-6647: the page reads this to tell a failed request from one that has not answered yet. A network error or
        // an empty body has no payload, and that must still read as a failure, so it is never left falsy. The body is not
        // for display: the thunk raises the fixed toast, and the page only asks whether this is set.
        paymentError: action.payload || true,
        preloaders: {
          ...state.preloaders,
          paymentMethods: false
        }
      };

    case payment.GET_PAYMENT_HISTORY:
      // eslint-disable-next-line no-case-declarations
      const lastPageNum = parseInt(queryString.parse(queryString.extract(action.payload.pagination.last_page)).page, 10);
      // eslint-disable-next-line no-case-declarations
      const currentPageNum = parseInt(queryString.parse(queryString.extract(action.payload.pagination.current_page)).page, 10);

      return {
        ...state,
        error: { ...state.error, paymentHistory: null },
        paymentHistory: action.payload.payments,
        paymentHistoryPagination: {
          currentPage: action.payload.pagination.current_page,
          currentPageNum,
          lastPage: action.payload.pagination.last_page,
          lastPageNum,
          perPage: action.payload.pagination.per_page,
          // Every payment across the pages, which json-api sends as `total` (Bootstrap::getPagination); the v2 footer reads "of n".
          total: action.payload.pagination.total
        },
        preloaders: {
          ...state.preloaders,
          paymentHistory: false
        }
      };

    case payment.GET_PAYMENT_HISTORY_FAIL:
      return {
        ...state,
        error: { ...state.error, paymentHistory: action.payload ?? true },
        preloaders: {
          ...state.preloaders,
          paymentHistory: false
        }
      };

    case payment.UPDATE_PAYMENT_METHOD:
      return {
        ...state,
        error: { ...state.error, updatePaymentMethod: null },
        preloaders: {
          ...state.preloaders,
          updatePaymentMethod: false
        },
        modals: {
          ...state.modals,
          updatePaymentMethod: false
        },
        success: { ...state.success, updatePaymentMethod: true }
      };

    case payment.UPDATE_PAYMENT_METHOD_FAIL:
      return {
        ...state,
        error: { ...state.error, updatePaymentMethod: action.payload ?? true },
        preloaders: {
          ...state.preloaders,
          updatePaymentMethod: false
        }
      };

    // GROWTH-6665: the page has shown the "subscription updated" message, so the one-shot flag is handed back. Left on, every later
    // change to `success` (a card added, a card removed) read as another subscription change, and a second save was no change at
    // all. assignCardToSubscription dispatches this about 1 ms after UPDATE_PAYMENT_METHOD, never in the same turn.
    case payment.UPDATE_PAYMENT_METHOD_RESET:
      return {
        ...state,
        success: { ...state.success, updatePaymentMethod: null }
      };

    case payment.ADD_CARD_SUCCESS:
      return {
        ...state,
        success: { ...state.success, addCard: action.payload },
        error: { ...state.error, addCard: null }
      };

    case payment.ADD_CARD_FAIL:
      return {
        ...state,
        error: { ...state.error, addCard: action.payload }
      };

    case payment.SET_DEFAULT_SOURCE: {
      const mapDefaultFlags = (methods, defaultId) => {
        if (!methods?.length || !defaultId) {
          return methods;
        }

        return methods.map(card => ({
          ...card,
          default_source: card.id === defaultId
        }));
      };

      if (action.payload?.object === "customer") {
        const defaultId = action.payload.default_source;
        const defaultCard = state.paymentMethods?.find(({ id }) => id === defaultId);

        return {
          ...state,
          defaultSource: defaultCard,
          paymentMethods: mapDefaultFlags(state.paymentMethods, defaultId)
        };
      }

      const defaultId = action.payload?.id;

      return {
        ...state,
        defaultSource: action.payload,
        paymentMethods: mapDefaultFlags(state.paymentMethods, defaultId)
      };
    }

    case payment.ADD_CARD_LOCALLY: {
      const allPaymentMethods = state.paymentMethods ? state.paymentMethods : [];
      return {
        ...state,
        paymentMethods: [action.payload, ...allPaymentMethods]
      };
    }

    case payment.UPDATE_CARD_SUCCESS:
      return {
        ...state,
        success: { ...state.success, updateCard: action.payload }
      };

    case payment.UPDATE_CARD_LOCALLY: {
      const changedCard = state.paymentMethods.map(card => {
        if (card.id === action.payload.id) {
          return { ...action.payload };
        }
        return { ...card, default_source: action.payload.default_source ? false : card.default_source };
      });

      return {
        ...state,
        paymentMethods: changedCard
      };
    }

    case payment.UPDATE_CARD_FAIL:
      return {
        ...state,
        error: { ...state.error, updateCard: action.payload ?? true }
      };

    case payment.DELETE_CARD_SUCCESS:
      return {
        ...state,
        success: { ...state.success, deleteCard: action.payload }
      };

    case payment.DELETE_CARD_LOCALLY: {
      const cardToBeDeletedIndex = state.paymentMethods.findIndex(card => card.id === action.payload.id);

      state.paymentMethods.splice(cardToBeDeletedIndex, 1);
      return {
        ...state,
        paymentMethods: [...state.paymentMethods]
      };
    }

    case payment.CHARGE_FOR_MEMBERSHIP_SUCCESS:
      return {
        ...state,
        success: { ...state.success, chargeForMembership: true },
        preloaders: {
          ...state.preloaders,
          chargeForMembership: false
        }
      };

    case payment.CHARGE_FOR_MEMBERSHIP_FAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          chargeForMembership: false
        },
        error: {
          ...state.error,
          chargeForMembership: action.payload
        }
      };

    case payment.SET_STRIPE_TOKEN:
      return {
        ...state,
        stripeToken: action.payload
      };

    case payment.GET_ERROR_TOKEN:
      return {
        ...state,
        error: {
          ...state.error,
          stripeTokenError: action.payload
        }
      };

    case payment.CLEAR_ERROR_TOKEN:
      return {
        ...state,
        error: {
          ...state.error,
          stripeTokenError: null
        }
      };

    // GROWTH-6669: the payment-success modals dispatch this while a team owner can still be selected, so the data goes but
    // ownerId stays; resetting it too would let the owner's next cards survive the switch back to the own account.
    case payment.CLEAR_STATE:
      return {
        ...defaultState,
        ownerId: state.ownerId
      };

    // GROWTH-6669: forgets the one-shot feedback (the success flags and the errors) and keeps the data, so a visit to
    // /payments leaves the cards for the payment modals and a pro-bono charge does not empty its own card list. CLEAR_STATE
    // above is the full reset.
    case payment.CLEAR_SUCCESS:
      return {
        ...state,
        success: { ...defaultState.success },
        error: defaultState.error,
        paymentError: defaultState.paymentError
      };

    // GROWTH-6669: the data here belongs to one account. When the member switches to another (the account selector, a
    // ?user_id= link, Settings' "Switch to my account"), drop it before anything can show it under the new account. A
    // repeat for the same owner (the role arriving after a ?user_id= load) keeps everything.
    case teams.SET_TEAM: {
      const ownerId = action.payload?.value ? String(action.payload.value) : "";

      return ownerId === state.ownerId ? state : { ...defaultState, ownerId };
    }

    // PaymentHistory empties its own cache when it closes. CLEAR_STATE resets the whole slice, cards included.
    case payment.RESET_PAYMENT_HISTORY:
      return {
        ...state,
        paymentHistory: defaultState.paymentHistory,
        paymentHistoryPagination: defaultState.paymentHistoryPagination
      };

    default:
      return state;
  }
}
